/**
 * 改收件人电话。
 *
 * ★ 用户报的:电话写错了,原来只能**取消整单重下** ——
 *   而单号变了、司机白跑一趟,只为了改一个号码。
 *
 * ★ 同步通讯录是**另一件事**:订单那边先改完,通讯录那步单独来。
 *   通讯录没更新成功不该让人以为「电话没改成」——
 *   他会再改一次,而那次会报「和原来的号码一样」。
 */
import { useState } from 'react';
import { ApiFailure, api } from '../lib/api';

export function ChangePhoneDialog({
  orderId,
  current,
  receiverName,
  onClose,
  onDone,
}: {
  orderId: string;
  current: string;
  receiverName: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [phone, setPhone] = useState(current);
  /*
   * ★ 默认勾上。电话写错**多半是通讯录里就错了** ——
   *   只改这一单的话,下一单照样填错,而人以为已经修好了。
   */
  const [sync, setSync] = useState(true);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ phone: string; contact: { synced: boolean; error?: string } } | null>(
    null,
  );

  const changed = phone.trim() !== '' && phone.trim() !== current;

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{
        phone: string;
        contact: { synced: boolean; error?: string };
      }>(`/orders/${orderId}/receiver-phone`, {
        method: 'POST',
        body: { phone: phone.trim(), syncContact: sync, ...(note.trim() ? { note: note.trim() } : {}) },
      });
      setDone(r);
      await onDone();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '改电话失败');
    } finally {
      setBusy(false);
    }
  }

  const INPUT =
    'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-label="改收件人电话"
        className="w-full max-w-md rounded-xl bg-white p-5 dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">改电话</h2>
        <p className="mt-1 text-sm text-slate-500">{receiverName} · 现在是 {current}</p>

        {done ? (
          <div className="mt-4 space-y-2 text-sm">
            <div className="rounded-lg bg-green-50 px-3 py-2 text-green-800
                            dark:bg-green-950/40 dark:text-green-300">
              这一单的电话已经改成 {done.phone}
            </div>
            {/*
              ★ 两件事分开说。通讯录没更新不影响这一单已经改好了 ——
                合成一句「失败」的话,人会以为电话没改成,然后再改一次。
            */}
            <div
              className={`rounded-lg px-3 py-2 ${
                done.contact.synced
                  ? 'bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-300'
                  : 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
              }`}
            >
              {done.contact.synced
                ? '通讯录里那条也改了,旧号存成了「旧号」—— 对方拿旧号打来照样搜得到'
                : `通讯录没改:${done.contact.error ?? '未知原因'}(这一单的电话不受影响)`}
            </div>
            <div className="flex justify-end pt-2">
              <button
                onClick={onClose}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white"
              >
                知道了
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-4 space-y-3">
              <label className="block text-sm">
                <span className="mb-1 block text-slate-600 dark:text-slate-300">新号码</span>
                <input
                  aria-label="新号码"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={INPUT}
                />
              </label>

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  aria-label="同时更新通讯录"
                  className="mt-1"
                  checked={sync}
                  onChange={(e) => setSync(e.target.checked)}
                />
                <span>
                  <span className="text-slate-800 dark:text-slate-100">同时更新通讯录里的号码</span>
                  <span className="block text-xs text-slate-500">
                    电话写错多半是通讯录里就错了 —— 只改这一单的话,下一单照样填错。
                    旧号会存成「旧号」,对方拿旧号打来还搜得到
                  </span>
                </span>
              </label>

              <label className="block text-sm">
                <span className="mb-1 block text-slate-600 dark:text-slate-300">为什么改(可不填)</span>
                <input
                  aria-label="改电话的原因"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="客户来电更正"
                  className={INPUT}
                />
              </label>

              {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-slate-500">
                取消
              </button>
              <button
                onClick={() => void save()}
                disabled={busy || !changed}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white
                           disabled:opacity-40"
              >
                {busy ? '保存中…' : '改'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
